import { useEffect, useRef, useState } from "react";

import * as S from "./styles";
import { Controller, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { Input } from "@/components/Input";

import {
  FaChevronLeft,
  FaEnvelope,
  FaLock,
  FaPhone,
  FaUser,
} from "react-icons/fa";
import { FcGoogle } from "react-icons/fc";
import { ReactComponent as SvgFacebook } from "@/assets/svgs/facebook.svg";

import quote from "@/assets/quote.png";
import { ReactComponent as SuccessSvg } from "@/assets/svgs/success-circle.svg";

import { useAuth } from "@/context/AuthContext";
import { FutButton } from "@/components/FutButton";

import RoundInput from "@/components/RoundInput";
import {
  CircularProgress,
  Divider,
  IconButton,
  InputAdornment,
  Modal,
  Radio,
  RadioGroup,
} from "@mui/material";
import {
  PASSWORD_MATCH,
  REQUIRED_FIELD,
  VALID_EMAIL,
} from "@/constants/yupErrors";
import { NavLink, useNavigate } from "react-router-dom";
import {
  MdArrowBackIosNew,
  MdVisibility,
  MdVisibilityOff,
} from "react-icons/md";
import { toast } from "react-toastify";
import { plans } from "@/constants/plans";
import { BsArrowLeft, BsFillCreditCard2BackFill } from "react-icons/bs";
import { api } from "@/services/api";
import { catchError } from "@/utils/catchError";
import PlansChooser from "@/components/PlansChooser";
import { FaPix } from "react-icons/fa6";
import { setCookie } from "@/utils/cookies";
import LoginComponent from "@/components/Login";

const loginSchema = yup.object({
  email: yup.string().required(REQUIRED_FIELD).email(VALID_EMAIL),
  password: yup.string().required(REQUIRED_FIELD),
});

type FormInputs = yup.InferType<typeof loginSchema>;

export function Login() {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [resetPassScreen, setResetPassScreen] = useState(false);
  const [planType, setPlanType] = useState("");
  const [userId, setUserId] = useState(0);
  const [step, setStep] = useState(0);
  const [modal, setModal] = useState(false);
  const { signIn, isAuthenticated } = useAuth();
  const navigate = useNavigate();

  const containerRef = useRef<HTMLDivElement>(null);
  const affiliatedId = localStorage.getItem("affiliatedId")
    ? localStorage.getItem("affiliatedId")
    : null;
  const loginForm = useForm<FormInputs>({
    resolver: yupResolver(loginSchema),
    defaultValues: {
      email: "",
      password: "",
    },
  });

  const changePass = useForm({
    defaultValues: {
      email: "",
    },
  });

  const paymentForm = useForm({
    defaultValues: {
      payment: "CREDIT_CARD",
    },
  });

  const loginErrors = loginForm.formState.errors;
  const changePassErrors = changePass.formState.errors;

  useEffect(() => {
    if (isAuthenticated) {
      navigate("/dashboard");
    }
  }, []);

  async function submitLogin(data: FormInputs) {
    const { email, password } = data;
    setIsSubmitting(true);

    const hasError = await signIn({ email, password });
    if (hasError?.error.status === 402) {
      setModal(true);
      setUserId(hasError.error.userId);
    }

    setIsSubmitting(false);
  }

  async function submitChangePass(data: { email: string }) {
    try {
      setIsSubmitting(true);
      await api.post("/user/requestNewPassword", {
        email: data.email,
      });
      toast.success(
        "Email enviado com sucesso! Cheque sua caixa de mensagens ou a caixa de spam"
      );
      setResetPassScreen(false);
      setIsSubmitting(false);
    } catch (e) {
      const message = catchError(e);
      if (message) {
        toast.error(message);
        return;
      }
      toast.error("Ocorreu um erro ao tentar enviar o email");
    }
  }

  const changeStep = (toStep: "prev" | "next") => {
    if (containerRef.current) {
      containerRef.current.style.opacity = "0";

      setTimeout(() => {
        if (toStep === "next") {
          setStep((prev) => prev + 1);
        }

        if (toStep === "prev") {
          setStep((prev) => prev - 1);
        }

        containerRef.current!.style.opacity = "1";
        setIsSubmitting(false);
      }, 400);
    }
  };

  const planHandler = async (plan: keyof typeof plans) => {
    setIsSubmitting(true);
    setPlanType(plan);
    changeStep("next");
  };

  const submitPayment = async (data: { payment: string }) => {
    try {
      setIsSubmitting(true);
      const response = await api.post("/payment/checkout", {
        lineItems: [
          { price: plans[planType as keyof typeof plans], quantity: 1 },
        ],
        userId,
        type_payment: data.payment,
      });
      if (data.payment === "PIX") {
        setCookie(
          "payment_data",
          JSON.stringify({
            userId: userId,
            planType: planType,
            pixKey: response.data,
          }),
          1
        );

        navigate("/telaPix");
      }

      if (data.payment === "CREDIT_CARD") {
        changeStep("next");
        setIsSubmitting(false);
        setTimeout(() => {
          window.location.replace(response.data);
        }, 5000);
      }

      setIsSubmitting(false);
    } catch (e) {
      setStep(0);
      setModal(false);
      setIsSubmitting(false);
      const message = catchError(e);
      if (message) {
        toast.error(message);
        return;
      }
      toast.error("Ocorreu um erro inesperado ao enviar sua solicitação");
    }
  };

  // const modalSteps = [
  //   {
  //     component: (
  //       <S.ValidSignature>
  //         <h2>Sua Assinatura Não é Valida</h2>

  //         <p>
  //           Parece que sua assinatura acabou ou você ainda não fez o pagamento,
  //           volte agora a ser um membro da equipe e tenha todos os benefícios
  //           que a Futscore te oferece.
  //         </p>

  //         <FutButton fullWidth onClick={() => changeStep("next")}>
  //           Assinar Agora
  //         </FutButton>
  //       </S.ValidSignature>
  //     ),
  //   },
  //   {
  //     component: (
  //       <>
  //         <PlansChooser
  //           planHandler={(plan) => planHandler(plan)}
  //           disabled={isSubmitting}
  //         />
  //       </>
  //     ),
  //   },
  //   {
  //     component: (
  //       <S.PaymentContainer onSubmit={paymentForm.handleSubmit(submitPayment)}>
  //         <Controller
  //           name="payment"
  //           control={paymentForm.control}
  //           render={({ field }) => (
  //             <RadioGroup sx={{ width: "250px" }} {...field}>
  //               <S.RadioContainer>
  //                 <Radio value="CREDIT_CARD" />
  //                 <S.RadioNameContainer>
  //                   <BsFillCreditCard2BackFill />
  //                   Cartão de Crédito
  //                 </S.RadioNameContainer>
  //               </S.RadioContainer>

  //               <S.RadioContainer>
  //                 <Radio value="PIX" />
  //                 <S.RadioNameContainer>
  //                   <FaPix />
  //                   Pix
  //                 </S.RadioNameContainer>
  //               </S.RadioContainer>

  //               <FutButton
  //                 type="submit"
  //                 isLoading={isSubmitting}
  //                 fullWidth
  //                 sx={{ mt: 2 }}
  //               >
  //                 Finalizar Compra
  //               </FutButton>
  //             </RadioGroup>
  //           )}
  //         />
  //       </S.PaymentContainer>
  //     ),
  //   },
  //   {
  //     component: (
  //       <>
  //         {isSubmitting ? (
  //           <S.SuccessContainer style={{ padding: 30 }}>
  //             <CircularProgress />
  //           </S.SuccessContainer>
  //         ) : (
  //           <S.SuccessContainer>
  //             <SuccessSvg />
  //             <h2>
  //               Em instantes você será redirecionado para tela de checkout
  //             </h2>
  //           </S.SuccessContainer>
  //         )}
  //       </>
  //     ),
  //   },
  // ];

  return (
    <>
      <S.BackgroundVector />
      <S.QuoteContainer>
        <img src={quote} alt='"' />
        <h2>
          Vencer é uma mistura de luta, esforço, otimismo e não desistir nunca.
        </h2>
        <p>- Amby Burfoot</p>
      </S.QuoteContainer>
      <S.ContainerCont>
        <LoginComponent />
      </S.ContainerCont>
    </>
  );
}
